import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Zap, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const COLORS = {
    NanoCredit: "#00D084", FlexCredit: "#0066FF", BizCredit: "#FF6B00",
    SalaryAdvance: "#FFD600", SplitPay: "#7000FF", PrimeCredit: "#FF0055",
    TradeCredit: "#06B6D4", StockFinance: "#F59E0B",
};

const STATUS_COLORS = {
    active: "bg-blue-50 text-blue-700 border-blue-200",
    repaid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    overdue: "bg-rose-50 text-rose-700 border-rose-200",
    declined: "bg-slate-100 text-slate-700 border-slate-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    disbursed: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function AdminLoans() {
    const [filter, setFilter] = useState("all");
    const [loans, setLoans] = useState([]);

    const refresh = () => {
        const url = filter === "all" ? "/admin/loans" : `/admin/loans?status=${filter}`;
        api.get(url).then((r) => setLoans(r.data));
    };
    useEffect(() => { refresh(); }, [filter]);

    const setStatus = async (id, status) => {
        await api.post(`/admin/loans/${id}/decision`, { status });
        toast.success(`Loan ${status}`);
        refresh();
    };

    const [sweeping, setSweeping] = useState(false);
    const autoCollect = async () => {
        setSweeping(true);
        try {
            const r = await api.post("/admin/loans/auto-collect");
            toast.success(
                `Sweep done — ${r.data.marked_overdue} overdue, ${r.data.auto_debited} auto-debited (₦${(r.data.auto_debited_amount || 0).toLocaleString()})`
            );
            refresh();
        } catch (err) {
            toast.error("Sweep failed");
        } finally {
            setSweeping(false);
        }
    };

    return (
        <div data-testid="admin-loans-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930]">Loans</h1>
                    <p className="text-sm text-slate-500 mt-1">{loans.length} loans</p>
                </div>
                <AlertDialog>
                    <AlertDialogTrigger asChild>
                        <Button className="bg-amber-500 hover:bg-amber-600 font-semibold rounded-xl" data-testid="auto-collect-btn">
                            <Zap size={16} /> Run auto-collection sweep
                        </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="font-display">Run auto-collection sweep?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will: (1) mark active loans past their due date as <strong>overdue</strong>, (2) apply <strong>5%/week late fees</strong> on overdue loans (capped at 30% of principal), and (3) <strong>auto-debit</strong> available wallet balance from each overdue borrower. Notifications + events are logged for every action.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="sweep-cancel">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={autoCollect} disabled={sweeping} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="sweep-confirm">
                                {sweeping ? "Sweeping…" : "Yes, run sweep"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <Tabs value={filter} onValueChange={setFilter} className="mb-4">
                <TabsList className="bg-white border border-slate-200 rounded-xl p-1" data-testid="loans-filter">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="active">Active</TabsTrigger>
                    <TabsTrigger value="repaid">Repaid</TabsTrigger>
                    <TabsTrigger value="overdue">Overdue</TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden product-shadow">
                <div className="overflow-x-auto admin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                                <th className="px-5 py-3">Borrower</th>
                                <th className="px-5 py-3">Product</th>
                                <th className="px-5 py-3">Amount</th>
                                <th className="px-5 py-3">Repaid</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Disbursed</th>
                                <th className="px-5 py-3">Due</th>
                                <th className="px-5 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loans.map((l) => (
                                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`loan-row-${l.id}`}>
                                    <td className="px-5 py-3 font-medium">
                                        <Link to={`/admin/loans/${l.id}`} className="hover:underline flex items-center gap-1.5 text-[#0A1930]" data-testid={`loan-open-${l.id}`}>
                                            {l.applicant}
                                            <ExternalLink size={11} className="text-slate-400" />
                                        </Link>
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className="px-2 py-1 rounded-md text-xs font-bold" style={{ color: COLORS[l.product] || "#0066FF", background: `${COLORS[l.product] || "#0066FF"}15` }}>{l.product}</span>
                                    </td>
                                    <td className="px-5 py-3 font-mono font-semibold">{fmtCurrency(l.amount)}</td>
                                    <td className="px-5 py-3 font-mono text-slate-600">{fmtCurrency(l.repaid_amount || 0)}</td>
                                    <td className="px-5 py-3"><Badge className={`capitalize border ${STATUS_COLORS[l.status]}`}>{l.status}</Badge></td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(l.disbursed_at)}</td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(l.due_date)}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex gap-1">
                                            <Link to={`/admin/loans/${l.id}`}>
                                                <Button variant="outline" size="sm" className="h-7 text-xs" data-testid={`open-detail-${l.id}`}>Open</Button>
                                            </Link>
                                            {l.status === "active" && (
                                                <Button onClick={() => setStatus(l.id, "overdue")} variant="outline" size="sm" className="h-7 text-xs" data-testid={`mark-overdue-${l.id}`}>Overdue</Button>
                                            )}
                                            {l.status === "overdue" && (
                                                <Button onClick={() => setStatus(l.id, "active")} variant="outline" size="sm" className="h-7 text-xs" data-testid={`reset-${l.id}`}>Reset</Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
